import { Policy } from '@commandDrivenStateEngine/core/policies';
import { ClientLibraryType } from '@bett3r-dev/teselly-client-library';
import {
  SalesChannelSetupState,
  TriggerEvent,
  WAREHOUSE_RULE_DEFINITION_IDS,
  LOGISTICS_RULE_DEFINITION_IDS,
  WAREHOUSE_RULE_DEFINITION_ID_SET,
  LOGISTICS_RULE_DEFINITION_ID_SET,
  BILLING_RULE_DEFINITION_IDS,
  BILLING_RULE_DEFINITION_ID_SET,
  CHANNEL_STOCK_RULE_DEFINITION_ID
} from '../state';
import { SalesChannelSetupCommand } from '../commands';
import { GetActiveChannelId } from '../store';

/**
 * Sales-channel setup init policy (TV1-1904, rule-based rework).
 *
 * Responsibilities:
 * - Subscribe to the system-integrations readmodel; sync the open channel
 *   (settings snapshot, billingEntityId, completion marker) via `data/set`.
 * - Load catalogs: warehouses, billing entities, logistics + invoicing
 *   integrations, and sales-order statuses.
 * - Pre-fill the inventory/logistics steps from the channel's EXISTING automation
 *   rules (`rules/sync`) so a re-run reflects the current config and updates the
 *   same rule (the rule's `ruleId` is carried for an in-place `SetRule`).
 */
export const createSetupInitPolicy = (
  backend: ClientLibraryType,
  getActiveChannelId: GetActiveChannelId
): Policy<SalesChannelSetupState, SalesChannelSetupCommand> => ({
  name: 'sales-channel-setup-init',

  init: ({ dispatch }) => {
    const syncFromChannel = ( channel: any ) => {
      const activeId = getActiveChannelId();
      if ( !channel || !activeId || channel.id !== activeId ) return;

      const settings = ( channel.settings ?? {}) as Record<string, unknown>;
      dispatch(
        {
          type: 'salesChannelSetup/data/set',
          payload: {
            integrationId: channel.id ?? null,
            accountId: channel.accountId ?? null,
            connectorId: channel.connectorId ?? null,
            integrationUserId: channel.integrationUserId ?? null,
            channelSetupCompletedAt:
              ( settings.channelSetupCompletedAt as string | undefined ) ?? null,
            currentSettings: settings,
            currentBillingEntityId: channel.billingEntityId ?? null
          }
        },
        { source: 'api' }
      );
    };

    const subscription = backend.subscriptions.ExternalSystemsIntegrations(
      {},
      ( change: any ) => {
        if ( change?.type === 'snapshot' ) {
          const activeId = getActiveChannelId();
          const match = ( change.data?.items ?? []).find(
            ( c: any ) => c.id === activeId
          );
          if ( match ) syncFromChannel( match );
          return;
        }
        if ( change?.type === 'signal' ) return;
        if ( change?.type === 'delete' ) return;
        if ( change?.data ) syncFromChannel( change.data );
      }
    );

    // Load catalogs (best-effort; failures leave empty selects).
    const loadCatalogs = async () => {
      try {
        const warehouses = ( await backend.queries.StockManagementWarehouses()) as any[];
        dispatch(
          {
            type: 'salesChannelSetup/catalogs/setWarehouses',
            payload: {
              warehouses: ( warehouses ?? []).map(( w: any ) => ({
                id: w.id,
                label: w.name ?? w.id
              }))
            }
          },
          { source: 'api' }
        );
      } catch ( error ) {
        console.error( '[salesChannelSetup] Failed to load warehouses:', error );
      }

      try {
        const entities = ( await backend.queries.BillingEntities()) as any[];
        dispatch(
          {
            type: 'salesChannelSetup/catalogs/setBillingEntities',
            payload: {
              billingEntities: ( entities ?? []).map(( e: any ) => ({
                id: e.id,
                name: e.name ?? e.legalName ?? e.id
              }))
            }
          },
          { source: 'api' }
        );
      } catch ( error ) {
        console.error( '[salesChannelSetup] Failed to load billing entities:', error );
      }

      try {
        const statuses = ( await backend.queries.SalesOrderStatuses()) as any[];
        dispatch(
          {
            type: 'salesChannelSetup/catalogs/setOrderStatuses',
            payload: {
              orderStatuses: ( statuses ?? []).map(( s: any ) => ({
                id: s.id,
                name:
                  typeof s.name === 'string'
                    ? s.name
                    : ( s.name?.es ?? s.name?.en ?? s.id )
              }))
            }
          },
          { source: 'api' }
        );
      } catch ( error ) {
        console.error( '[salesChannelSetup] Failed to load order statuses:', error );
      }
    };

    // Pre-fill the inventory/logistics steps from the channel's existing rules.
    const loadRules = async () => {
      const channelId = getActiveChannelId();
      if ( !channelId ) return;
      try {
        const rules = ( await backend.queries.Rules({
          filter: [{ field: 'hierarchyEntityIds', op: 'contains', value: channelId }]
        })) as any[];

        const triggerFor = ( ruleDefinitionId: string, statusVariantId: string ): TriggerEvent =>
          ruleDefinitionId === statusVariantId ? 'OrderStatusChanged' : 'OrderPlaced';

        const warehouseRule = ( rules ?? []).find(( r: any ) =>
          WAREHOUSE_RULE_DEFINITION_ID_SET.has( r.ruleDefinitionId ));
        const logisticsRule = ( rules ?? []).find(( r: any ) =>
          LOGISTICS_RULE_DEFINITION_ID_SET.has( r.ruleDefinitionId ));
        const channelStockRule = ( rules ?? []).find(( r: any ) =>
          r.ruleDefinitionId === CHANNEL_STOCK_RULE_DEFINITION_ID );

        const warehouse = warehouseRule
          ? {
              warehouseId:
                warehouseRule.declaredInputsValues?.warehouse?.warehouseId ?? null,
              trigger: triggerFor(
                warehouseRule.ruleDefinitionId,
                WAREHOUSE_RULE_DEFINITION_IDS.OrderStatusChanged
              ),
              statusId:
                warehouseRule.declaredInputsValues?.desiredStatus?.statusId ?? null,
              ruleId: warehouseRule.ruleId ?? warehouseRule.id ?? null,
              // Paired channel-stock rule: carried so a re-run updates it in place.
              channelStockRuleId:
                channelStockRule?.ruleId ?? channelStockRule?.id ?? null
            }
          : null;

        const logistics = logisticsRule
          ? (() => {
              const provider = logisticsRule.declaredInputsValues?.logisticsProvider;
              const logisticsIntegrationId =
                provider?.connectorId && provider?.integrationUserId
                  ? `${provider.connectorId}_${provider.integrationUserId}`
                  : null;
              return {
                logisticsIntegrationId,
                trigger: triggerFor(
                  logisticsRule.ruleDefinitionId,
                  LOGISTICS_RULE_DEFINITION_IDS.OrderStatusChanged
                ),
                statusId:
                  logisticsRule.declaredInputsValues?.desiredStatus?.statusId ?? null,
                ruleId: logisticsRule.ruleId ?? logisticsRule.id ?? null
              };
            })()
          : null;

        const billingRule = ( rules ?? []).find(
          ( r: any ) => BILLING_RULE_DEFINITION_ID_SET.has( r.ruleDefinitionId )
        );
        const billing = billingRule
          ? {
              trigger: triggerFor(
                billingRule.ruleDefinitionId,
                BILLING_RULE_DEFINITION_IDS.OrderStatusChanged
              ),
              statusId: billingRule.declaredInputsValues?.value1?.statusId ?? null,
              ruleId: billingRule.ruleId ?? billingRule.id ?? null
            }
          : null;

        if ( warehouse || logistics || billing ) {
          dispatch(
            { type: 'salesChannelSetup/rules/sync', payload: { warehouse, logistics, billing }},
            { source: 'api' }
          );
        }
      } catch ( error ) {
        console.error( '[salesChannelSetup] Failed to load channel rules:', error );
      }
    };

    // Logistics + invoicing integrations come from the same system-integrations
    // readmodel: logistics filtered to `connectorKind === 'logistics'` (carrying
    // connectorId/integrationUserId so the rule's `logisticsProvider` input can be
    // built), invoicing to rows that issue invoices.
    //
    // A live item map is kept so INCREMENTAL changes (create/update/delete), not
    // just the initial snapshot, keep both catalogs fresh — otherwise an inline
    // logistics registration / invoicing provision never appears (the readmodel
    // emits a single-row change, not a fresh snapshot). Mirrors the canonical
    // `logisticsListLoadPolicy` change handling.
    const integrationItems = new Map<string, any>();

    const pushIntegrationCatalogs = () => {
      const items = [...integrationItems.values()];

      const logisticsIntegrations = items
        .filter(( i: any ) => i.connectorKind === 'logistics' )
        .map(( i: any ) => ({
          id: i.id,
          label: i.label ?? i.connectorId ?? i.id,
          connectorId: i.connectorId,
          integrationUserId: i.integrationUserId
        }));
      dispatch(
        {
          type: 'salesChannelSetup/catalogs/setLogistics',
          payload: { logisticsIntegrations }
        },
        { source: 'api' }
      );

      const invoicingIntegrations = items
        .filter(
          ( i: any ) =>
            ( Array.isArray( i.capabilities ) && i.capabilities.includes( 'issues-invoices' )) ||
            i.connectorKind === 'invoicing'
        )
        .map(( i: any ) => ({
          id: i.id,
          label: i.label ?? i.connectorId ?? i.id
        }));
      dispatch(
        {
          type: 'salesChannelSetup/catalogs/setInvoicingIntegrations',
          payload: { invoicingIntegrations }
        },
        { source: 'api' }
      );
    };

    const integrationsSub = backend.subscriptions.ExternalSystemsIntegrations(
      {},
      ( change: any ) => {
        if ( change?.type === 'snapshot' ) {
          integrationItems.clear();
          for ( const item of change.data?.items ?? []) {
            if ( item?.id ) integrationItems.set( item.id, item );
          }
          pushIntegrationCatalogs();
          return;
        }
        if ( change?.type === 'signal' ) return;
        if ( change?.type === 'delete' ) {
          const removedId = change.previous?.id;
          if ( removedId && integrationItems.delete( removedId )) {
            pushIntegrationCatalogs();
          }
          return;
        }
        if ( change?.data?.id ) {
          integrationItems.set( change.data.id, change.data );
          pushIntegrationCatalogs();
        }
      }
    );

    loadCatalogs();
    loadRules();

    return () => {
      subscription?.close?.();
      integrationsSub?.close?.();
    };
  }
});
